import type { ProductCategory, ProductTypeOption } from '@/domain/types';
import { requireSupabase } from '@/lib/supabase';

type TaxonomyRow = {
  id: string;
  name: string;
  slug: string;
  description: string;
  active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export interface TaxonomyInput {
  name: string;
  slug: string;
  description: string;
  active: boolean;
  sortOrder: number;
}

function mapCategory(row: TaxonomyRow): ProductCategory {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    description: row.description,
    active: row.active,
    sortOrder: row.sort_order,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapType(row: TaxonomyRow): ProductTypeOption {
  return mapCategory(row);
}

function toRow(input: TaxonomyInput) {
  return {
    name: input.name,
    slug: input.slug,
    description: input.description,
    active: input.active,
    sort_order: input.sortOrder,
    updated_at: new Date().toISOString(),
  };
}

export async function listCategories() {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('product_categories')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });

  if (error) throw error;
  return (data as TaxonomyRow[]).map(mapCategory);
}

export async function saveCategory(input: TaxonomyInput, id?: string) {
  const supabase = requireSupabase();
  const query = id
    ? supabase.from('product_categories').update(toRow(input)).eq('id', id)
    : supabase.from('product_categories').insert(toRow(input));
  const { data, error } = await query.select('*').single();

  if (error) throw error;
  return mapCategory(data as TaxonomyRow);
}

export async function deleteCategory(id: string) {
  const supabase = requireSupabase();
  const { error } = await supabase.from('product_categories').delete().eq('id', id);
  if (error) throw error;
}

export async function listProductTypes() {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('product_types')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });

  if (error) throw error;
  return (data as TaxonomyRow[]).map(mapType);
}

export async function saveProductType(input: TaxonomyInput, id?: string) {
  const supabase = requireSupabase();
  const query = id
    ? supabase.from('product_types').update(toRow(input)).eq('id', id)
    : supabase.from('product_types').insert(toRow(input));
  const { data, error } = await query.select('*').single();

  if (error) throw error;
  return mapType(data as TaxonomyRow);
}

export async function deleteProductType(id: string) {
  const supabase = requireSupabase();
  const { error } = await supabase.from('product_types').delete().eq('id', id);
  if (error) throw error;
}
